import { CircularProgress } from '@mui/material'
import AgoraUIKit, { layout } from 'amhm-vc-ui-kit'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { logJoinAuditRoom, logLeaveAuditRoom } from 'src/clinic/components/CheckIn/services'
import { markAuditorBookedEventCheckInLog } from 'src/consumer/services/featureServices'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { buildAuditorRtmUsername } from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/utils/rtmUsernameBuilder'

export default function MentorClinicAuditChamberRoom() {
  const { chamberRoomId, channelName } = useParams()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [agoraRtcProps, setAgoraRtcProps] = useState({})
  const [agoraRtmProps, setAgoraRtmProps] = useState({})
  const [loading, setLoading] = useState(true)
  const [sessionStarted, setSessionStarted] = useState(false)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [isAudioOpen, setIsAudioOpen] = useState(false)
  const auditRoomDetails = JSON.parse(localStorage.getItem('auditRoomDetails'))

  function handleRefreshStatusForUser() {
    markAuditorBookedEventCheckInLog(
      mentorProfileData?.id,
      auditRoomDetails?.checkedInMentorId,
      'REFRESH',
      {
        camera: isCameraOpen,
        mic: isAudioOpen,
      },
    )
  }

  const agoraCallbacks = {
    EndCall: async () => {
      await logLeaveAuditRoom(auditRoomDetails)
      await markAuditorBookedEventCheckInLog(
        mentorProfileData?.id,
        auditRoomDetails?.checkedInMentorId,
        'LEAVE',
        {
          camera: isCameraOpen,
          mic: isAudioOpen,
        },
      )
      window.history.back()
    },
  } // contains Agora Engine events as keys and callback functions as their values.

  async function getJoinData() {
    setLoading(true)
    const response = await getMPDataWithParams('/api/secure/teacher/audit/chamber-room/token', {
      chamberRoomId: chamberRoomId,
      channelName: channelName,
    })
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)
      await logJoinAuditRoom(auditRoomDetails)
      setAgoraRtcProps({
        appId: import.meta.env.VITE_AGORA_APP_ID,
        channel: channelName,
        uid: parseInt(data?.uid),
        token: data?.rtcToken,
        layout: layout.grid,
        role: 'host',
        enableAudio: false,
        enableVideo: false,
      })
      // Build RTM username with role, name, and profilePicId for auditor
      const rtmUsername = buildAuditorRtmUsername(mentorProfileData)
      
      // Parse to show the object being sent
      let parsedUsername = null
      try {
        parsedUsername = JSON.parse(rtmUsername)
      } catch (error) {
        console.error('Error parsing RTM username before sending:', error)
        parsedUsername = { error: 'Failed to parse' }
      }
      
      console.log('=== SETTING AGORA RTM PROPS (SENDER - Auditor) ===', {
        rawUsername: rtmUsername,
        parsedObject: parsedUsername,
        objectStructure: {
          role: parsedUsername?.role || 'MISSING',
          name: parsedUsername?.name || 'MISSING',
          profilePicId: parsedUsername?.profilePicId || 'MISSING (null)',
        },
        mentorProfileData: {
          id: mentorProfileData?.id,
          name: mentorProfileData?.name,
          firstName: mentorProfileData?.firstName,
          hasProfilePicId: !!(mentorProfileData?.profilePicId || mentorProfileData?.profilePictureId),
        },
      })
      
      setAgoraRtmProps({
        username: rtmUsername,
        displayUsername: true,
        token: data?.rtmToken,
      })
      setSessionStarted(true)
    } else {
      toast.error('Failed to get join data')
      window.history.back()
    }
    setLoading(false)
  }

  useEffect(() => {
    getJoinData()
    markAuditorBookedEventCheckInLog(
      mentorProfileData?.id,
      auditRoomDetails?.checkedInMentorId,
      'JOIN',
      {
        camera: isCameraOpen,
        mic: isAudioOpen,
      },
    )
  }, [])

  // Heartbeat mechanism - log refresh status every 3 minutes
  useEffect(() => {
    let interval = null
    if (sessionStarted) {
      handleRefreshStatusForUser()
      interval = setInterval(() => {
        handleRefreshStatusForUser()
      }, 180000) // 3 minutes = 180000 ms
    } else {
      clearInterval(interval)
    }
    return () => {
      clearInterval(interval)
    }
  }, [sessionStarted, isCameraOpen, isAudioOpen])

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <h3>Starting your audit session....</h3>
          <CircularProgress />
        </div>
      ) : (
        <AgoraUIKit
          rtcProps={agoraRtcProps}
          callbacks={agoraCallbacks}
          styleProps={{
            localBtnContainer: { backgroundColor: 'cream' },
          }}
          rtmProps={agoraRtmProps}
        />
      )}
    </div>
  )
}
